import type { Toman } from './api/types';

/* Display helpers.
 *
 * Persian digits everywhere a number is shown to a human, and a Jalali date for
 * anything the customer will read back to you on the phone — they are not going
 * to say "2027-08-27".
 */

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

export function toPersianDigits(input: string | number): string {
  return String(input).replace(/\d/g, (d) => FA_DIGITS[Number(d)]);
}

/**
 * Formats an amount for display. The input is a STRING of whole tomans and is
 * never parsed into a number — see the note in api/types.ts. Grouping is done on
 * the digits themselves so a value larger than Number.MAX_SAFE_INTEGER would
 * still render correctly rather than silently losing precision.
 */
export function formatToman(amount: Toman): string {
  const digits = (amount ?? '0').replace(/[^\d-]/g, '');
  const negative = digits.startsWith('-');
  const plain = negative ? digits.slice(1) : digits;
  const grouped = plain.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '−' : ''}${toPersianDigits(grouped)} تومان`;
}

/* ---------------------------------------------------------------------- *
 *  Jalali dates                                                           *
 * ---------------------------------------------------------------------- */

const BREAKS = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 1701, 1866, 2020, 2449,
  2703, 2812, 2938, 3162, 3762, 3900, 4094, 4494, 4824, 5064, 5384, 5768, 5828, 6116, 6448, 6767,
  7025, 7183, 7409, 7546, 8161, 8395, 8642, 9152, 9700, 10346];

/** Gregorian ISO date (YYYY-MM-DD) to Jalali, as ۱۴۰۵/۰۸/۱۲ */
export function toJalali(iso: string): string {
  const [gy, gm, gd] = iso.split('-').map(Number);
  if (!gy || !gm || !gd) return iso;
  const [jy, jm, jd] = gregorianToJalali(gy, gm, gd);
  const pad = (n: number) => String(n).padStart(2, '0');
  return toPersianDigits(`${jy}/${pad(jm)}/${pad(jd)}`);
}

function gregorianToJalali(gy: number, gm: number, gd: number): [number, number, number] {
  const g_d_m = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy = gy <= 1600 ? 0 : 979;
  const gy2 = gy <= 1600 ? gy - 621 : gy - 1600;
  const gm2 = gm > 2 ? gy2 + 1 : gy2;
  let days =
    365 * gy2 +
    Math.floor((gm2 + 3) / 4) -
    Math.floor((gm2 + 99) / 100) +
    Math.floor((gm2 + 399) / 400) -
    80 +
    gd +
    g_d_m[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return [jy, jm, jd];
}

/**
 * Any timestamp the server sends, as a Jalali date — never a Gregorian one.
 *
 * The API returns ISO-8601 with a timezone (`2026-08-04T09:54:33.000Z`), so the
 * date part cannot simply be sliced off: at 09:54 UTC it is already the next day
 * in Tehran, and slicing would show yesterday for anything logged after
 * mid-evening. Converting to LOCAL parts first is what makes the displayed day
 * match the day the person remembers.
 */
export function toJalaliDateTime(iso: string | null | undefined, withTime = true): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);

  const pad = (n: number) => String(n).padStart(2, '0');
  const [jy, jm, jd] = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
  const date = `${jy}/${pad(jm)}/${pad(jd)}`;
  if (!withTime) return toPersianDigits(date);
  return toPersianDigits(`${date} ${pad(d.getHours())}:${pad(d.getMinutes())}`);
}

/** The date only, for anywhere a time would just be noise. */
export function toJalaliDate(iso: string | null | undefined): string {
  return toJalaliDateTime(iso, false);
}

/** BREAKS is only used by leap-year maths a future report may need. */
export const JALALI_BREAKS = BREAKS;

/**
 * "۱۰۵ روز مانده" / "۱۲ روز گذشته".
 * Never a negative number: a customer reading "منفی ۱۲ روز" has to do the
 * arithmetic themselves to understand it.
 */
export function describeRemaining(daysRemaining: number, expired: boolean): string {
  if (expired || daysRemaining <= 0) return 'اشتراک پایان یافته';
  if (daysRemaining === 1) return 'فردا پایان می‌یابد';
  return `${toPersianDigits(daysRemaining)} روز مانده`;
}

export type SubscriptionTone = 'ok' | 'warning' | 'expired';

/** Matches the launcher's own colour thresholds so both tell the same story. */
export function subscriptionTone(daysRemaining: number, expired: boolean): SubscriptionTone {
  if (expired || daysRemaining <= 0) return 'expired';
  if (daysRemaining <= 30) return 'warning';
  return 'ok';
}

/** XXXXX-XXXXX / XXXX-XXXX-XXXX-XXXX, kept in Latin — it is typed, not read. */
export function formatCode(code: string): string {
  return code.toUpperCase();
}
